import React, { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { getProducts } from '../services/api';
import api from '../services/api';

const POLL_INTERVAL = 2000;

export default function LiveSession() {
  const [view, setView] = useState('list'); // list | dashboard
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [allInventory, setAllInventory] = useState([]);
  const [activeInv, setActiveInv] = useState(null);
  const [staging, setStaging] = useState([]); // [{fb_user_id, fb_user_name, qty, comment_id}]
  const [comments, setComments] = useState([]);
  const [processedIds, setProcessedIds] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showAddInv, setShowAddInv] = useState(false);
  const [invSearch, setInvSearch] = useState('');
  const [sessionForm, setSessionForm] = useState({ title: '', fb_live_url: '' });

  const extractVideoId = (url) => {
    if (!url) return '';
    // Match /videos/XXXX or ?v=XXXX or videos/XXXX
    const m = url.match(/\/videos\/(\d+)/) || url.match(/[?&]v=(\d+)/) || url.match(/(\d{10,})/);
    return m ? m[1] : '';
  };
  const [invForm, setInvForm] = useState({ product_id: '', live_code: '', selling_price: '', units_available: '', display_name: '' });
  const [addonModal, setAddonModal] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadSessions();
    getProducts().then(r => setProducts(r.data.products)).catch(() => {});
    api.get('/live/pages').then(r => setPages(r.data.pages)).catch(() => {
      setPages([{ id: 1, name: 'Page 1', code: 'P1' }, { id: 2, name: 'Page 2', code: 'P2' }]);
    });
  }, []);

  useEffect(() => {
    if (activeSession?.status === 'live') {
      pollRef.current = setInterval(async () => {
        await api.get('/live/sessions/'+activeSession.id+'/fetch-comments').catch(()=>{});
        loadComments(activeSession.id);
        loadStats(activeSession.id);
      }, POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [activeSession?.id, activeSession?.status]);

  async function loadSessions() {
    try { const r = await api.get('/live/sessions'); setSessions(r.data.sessions); } catch {}
  }

  async function loadInventory(sessionId) {
    try {
      const r = await api.get(`/live/sessions/${sessionId}/inventory`);
      setAllInventory(r.data.inventory);
    } catch {}
  }

  async function loadComments(sessionId) {
    try {
      const r = await api.get(`/live/sessions/${sessionId}/comments`);
      setComments(r.data.comments);
      // Restore processed state from DB
      const ids = new Set(r.data.comments.filter(c => c.is_processed).map(c => c.id));
      setProcessedIds(ids);
    } catch {}
  }

  async function loadStats(sessionId) {
    try { const r = await api.get(`/live/sessions/${sessionId}/stats`); setStats(r.data); } catch {}
  }

  async function fetchFbComments(sid) {
    const id = sid || activeSession?.id;
    if (!id) return;
    try { await api.get(`/live/sessions/${id}/fetch-comments`); } catch {}
  }

  async function openSession(s) {
    setActiveSession(s);
    setStaging([]);
    setActiveInv(null);
    setProcessedIds(new Set());
    await loadInventory(s.id);
    await loadComments(s.id);
    await loadStats(s.id);
    setView('dashboard');
    // Auto-fetch FB comments when opening a live session
    if (s.fb_video_id) {
      try {
        await api.get('/live/sessions/'+s.id+'/fetch-comments');
        await loadComments(s.id);
      } catch(e) {}
    }
  }

  async function createSession() {
    if (!extractVideoId(sessionForm.fb_live_url)) { toast.error('Please paste a valid Facebook Live URL'); return; }
    try {
      const r = await api.post('/live/sessions', { ...sessionForm, page_id: 1, fb_video_id: extractVideoId(sessionForm.fb_live_url), status: 'live' });
      toast.success('Session created!');
      setShowNewSession(false);
      setSessionForm({ title: '', fb_live_url: '' });
      loadSessions();
      openSession(r.data.session);
      setTimeout(() => { fetchFbComments(); }, 1000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  }

  async function startSession() {
    try {
      await api.post(`/live/sessions/${activeSession.id}/start`);
      setActiveSession(p => ({ ...p, status: 'live' }));
      toast.success('🔴 Session is LIVE!');
    } catch { toast.error('Failed'); }
  }

  async function endSession() {
    if (!window.confirm('End this live session?')) return;
    try {
      await api.post(`/live/sessions/${activeSession.id}/end`);
      setActiveSession(p => ({ ...p, status: 'ended' }));
      clearInterval(pollRef.current);
      toast.success('Session ended');
    } catch { toast.error('Failed'); }
  }

  async function addInventory() {
    if (!invForm.live_code || !invForm.product_id || !invForm.selling_price || !invForm.units_available) {
      toast.error('Fill in all fields'); return;
    }
    try {
      const r = await api.post(`/live/sessions/${activeSession.id}/inventory`, {
        ...invForm,
        product_id: parseInt(invForm.product_id),
        selling_price: parseFloat(invForm.selling_price),
        units_available: parseInt(invForm.units_available)
      });
      if (r.data.exceeds_master) toast('⚠️ Oversell mode — units exceed master stock', { icon: '⚠️' });
      else toast.success(`Code ${invForm.live_code} added`);
      setShowAddInv(false);
      setInvForm({ product_id: '', live_code: '', selling_price: '', units_available: '', display_name: '' });
      loadInventory(activeSession.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  }

// Add comment to staging list (+ button)
  async function addToStaging(comment) {
    // Mark as processed in DB (persistent)
    try {
      await api.post(`/live/sessions/${activeSession.id}/comments/${comment.id}/process`);
    } catch {}
    // Mark locally too for instant UI update
    setProcessedIds(p => new Set([...p, comment.id]));
    // Check if already in staging
    const existing = staging.find(s => s.fb_user_name === comment.fb_user_name);
    if (existing) {
      setStaging(p => p.map(s => s.fb_user_name === comment.fb_user_name ? { ...s, qty: s.qty + 1 } : s));
    } else {
      setStaging(p => [...p, {
        id: comment.id,
        fb_user_id: comment.fb_user_id,
        fb_user_name: comment.fb_user_name,
        fb_ranking: comment.fb_ranking || 'normal',
        fb_blacklisted: comment.fb_blacklisted || 0,
        comment_text: comment.comment_text,
        qty: 1
      }]);
    }
  }

  function updateStagingQty(index, delta) {
    setStaging(p => p.map((s, i) => {
      if (i !== index) return s;
      const newQty = s.qty + delta;
      return newQty < 1 ? null : { ...s, qty: newQty };
    }).filter(Boolean));
  }

  function removeFromStaging(index) {
    setStaging(p => p.filter((_, i) => i !== index));
  }

  // Checkout — bulk create orders for all staging customers with active inventory
  async function checkout() {
    if (!activeInv) { toast.error('Select a live inventory code first'); return; }
    if (staging.length === 0) { toast.error('No customers in staging list'); return; }
    try {
      const r = await api.post(`/live/sessions/${activeSession.id}/bulk-checkout`, {
        live_inventory_id: activeInv.id,
        customers: staging.map(s => ({
          fb_user_name: s.fb_user_name,
          fb_user_id: s.fb_user_id,
          quantity: s.qty
        }))
      });
      toast.success(`✅ ${staging.length} orders created!`);
      setStaging([]);
      setActiveInv(null);
      loadInventory(activeSession.id);
      loadStats(activeSession.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); }
  }

  // Addon — assign same customer to different code
  function openAddon(comment) {
    setAddonModal(comment);
  }

  function handleAddonSelect(inv) {
    if (!addonModal) return;
    const existing = staging.find(s => s.fb_user_name === addonModal.fb_user_name);
    if (existing) {
      toast('Customer already in staging. Switch active code to ' + inv.live_code + ' then checkout.');
    } else {
      setStaging(p => [...p, {
        id: addonModal.id,
        fb_user_id: addonModal.fb_user_id,
        fb_user_name: addonModal.fb_user_name,
        comment_text: addonModal.comment_text,
        qty: 1,
        override_inv: inv
      }]);
    }
    setAddonModal(null);
  }

  async function addManualComment(e) {
    e.preventDefault();
    const name = e.target.cname.value;
    const text = e.target.ctext.value;
    if (!name.trim()) return;
    try {
      const r = await api.post(`/live/sessions/${activeSession.id}/comments/manual`, { name, text });
      setComments(p => [...p, r.data.comment]);
      e.target.reset();
    } catch {}
  }

  const filteredInv = allInventory.filter(i =>
    i.live_code.includes(invSearch) || i.display_name?.toLowerCase().includes(invSearch.toLowerCase())
  );

  const inp = { background: '#f8f9fa', border: '1.5px solid #dee2e6', borderRadius: 8, padding: '8px 12px', color: '#212529', fontSize: 13, width: '100%', outline: 'none' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6c757d', marginBottom: 4, textTransform: 'uppercase' };

  // ── SESSION LIST VIEW ──────────────────────────────────────
  if (view === 'list') return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#212529' }}>Live Sessions</h1>
          <p style={{ color: '#6c757d', fontSize: 13 }}>Facebook Live order capture</p>
        </div>
        <button onClick={() => setShowNewSession(true)} style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ New Session</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sessions.map(s => (
          <div key={s.id} onClick={() => openSession(s)} style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 10, padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#212529' }}>{s.session_code}</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: s.status === 'live' ? '#ef444420' : s.status === 'ended' ? '#6b6d8520' : '#f59e0b20', color: s.status === 'live' ? '#ef4444' : s.status === 'ended' ? '#6c757d' : '#f59e0b' }}>
                {s.status === 'live' ? '🔴 LIVE' : s.status === 'ended' ? 'Ended' : 'Planned'}
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#212529', marginBottom: 4 }}>{s.title}</div>
            <div style={{ fontSize: 11, color: '#495057' }}>{s.page_name} · {s.created_at?.slice(0, 10)}</div>
          </div>
        ))}
        {sessions.length === 0 && (
          <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 14, padding: 40, textAlign: 'center', gridColumn: '1/-1' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📡</div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>No sessions yet</div>
            <div style={{ color: '#6c757d', fontSize: 13 }}>Create your first live session</div>
          </div>
        )}
      </div>

      {showNewSession && (
        <div style={{ position: 'fixed', inset: 0, background: '#000b', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 16, padding: 28, width: 440 }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 20 }}>New Live Session</div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Paste Facebook Live URL *</label>
              <input style={inp} value={sessionForm.fb_live_url}
                onChange={e => {
                  const url = e.target.value;
                  const vid = extractVideoId(url);
                  setSessionForm(p => ({ ...p, fb_live_url: url, title: p.title || (vid ? 'Live '+new Date().toLocaleDateString('en-GB') : '') }));
                }}
                placeholder="https://www.facebook.com/watch/live/?v=..." />
              {sessionForm.fb_live_url && (
                <div style={{ fontSize:11, marginTop:4, color: extractVideoId(sessionForm.fb_live_url) ? '#16a34a' : '#dc2626' }}>
                  {extractVideoId(sessionForm.fb_live_url) ? '✅ Video ID: '+extractVideoId(sessionForm.fb_live_url) : '⚠️ Could not detect video ID'}
                </div>
              )}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Title (optional)</label>
              <input style={inp} value={sessionForm.title} onChange={e => setSessionForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Crystal Live July 2" />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={createSession} style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', flex: 1, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Create</button>
              <button onClick={() => setShowNewSession(false)} style={{ background: '#dee2e6', color: '#212529', border: 'none', borderRadius: 8, padding: '10px', flex: 1, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── LIVE DASHBOARD VIEW ────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>

      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => { setView('list'); clearInterval(pollRef.current); }} style={{ background: '#dee2e6', border: 'none', borderRadius: 7, padding: '6px 12px', color: '#212529', cursor: 'pointer', fontSize: 12 }}>← Back</button>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#212529' }}>{activeSession?.session_code}</span>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: activeSession?.status === 'live' ? '#ef444420' : '#f59e0b20', color: activeSession?.status === 'live' ? '#ef4444' : '#f59e0b' }}>
            {activeSession?.status === 'live' ? '🔴 LIVE' : activeSession?.status === 'ended' ? '⬛ Ended' : '⏳ Planned'}
          </span>
          <span style={{ fontSize: 12, color: '#212529', fontWeight: 500 }}>{activeSession?.title}</span>
          <button onClick={() => { fetchFbComments(); loadComments(activeSession.id); }} style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 7, padding: '4px 10px', color: '#2e7d32', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>🔄 Sync</button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {stats && (
            <div style={{ display: 'flex', gap: 14, marginRight: 8 }}>
              {[['👥', stats.unique_customers, 'Customers'], ['📦', stats.total_items, 'Items'], ['💰', `$${stats.total_revenue.toFixed(2)}`, 'Revenue']].map(([icon, val, label]) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{icon} {val}</div>
                  <div style={{ fontSize: 10, color: '#495057' }}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {activeSession?.status === 'live' && <button onClick={endSession} style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>⬛ End</button>}
          {activeSession?.status === 'ended' && <button onClick={() => window.location.href='/postlive?session='+activeSession.id} style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>📋 Post-Live Fill</button>}
        </div>
      </div>

      {/* Main 2-panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 12, flex: 1, minHeight: 0 }}>

        {/* LEFT — Active inv + Staging + Checkout */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>

          {/* Active inventory display */}
          <div style={{ background: '#ffffff', border: activeInv ? '2px solid #7c6af5' : '1px solid #dee2e6', borderRadius: 12, padding: 14, flexShrink: 0 }}>
            {activeInv ? (
              <div>
                <div style={{ fontSize: 11, color: '#374151', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 }}>Active Code</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: 22, fontWeight: 700, color: '#a78bf8' }}>Code {activeInv.live_code}</span>
                    <div style={{ fontSize: 13, color: '#212529', marginTop: 2 }}>{activeInv.display_name}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>${activeInv.selling_price.toFixed(2)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: activeInv.units_remaining <= 0 ? '#ef4444' : '#22c55e' }}>{activeInv.units_remaining} left</div>
                    <div style={{ fontSize: 11, color: '#495057' }}>{activeInv.units_sold}/{activeInv.units_available} sold</div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#6c757d', fontSize: 13, padding: '8px 0' }}>
                👇 Select a code from inventory below
              </div>
            )}
          </div>

          {/* Staging list */}
          <div style={{ flex: 1, overflow: 'auto', background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase', marginBottom: 8 }}>
              Staging List ({staging.length} customers)
            </div>
            {staging.length === 0 && (
              <div style={{ color: '#6c757d', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
                Click + on comments to add customers
              </div>
            )}
            {staging.map((s, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e9ecef' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                    {(s.fb_user_name || '?')[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.fb_user_name}</span>
                  {s.override_inv && <span style={{ fontSize: 10, color: '#a78bf8', background: '#7c6af520', borderRadius: 10, padding: '2px 6px' }}>Code {s.override_inv.live_code}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <button onClick={() => updateStagingQty(idx, -1)} style={{ width: 22, height: 22, background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontSize: 13, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{s.qty}</span>
                  <button onClick={() => updateStagingQty(idx, 1)} style={{ width: 22, height: 22, background: '#22c55e20', color: '#22c55e', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                  <button onClick={() => removeFromStaging(idx)} style={{ width: 22, height: 22, background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginLeft: 4 }}>🗑</button>
                </div>
              </div>
            ))}
          </div>

          {/* Checkout button */}
          <button onClick={checkout} disabled={staging.length === 0 || !activeInv} style={{ background: staging.length > 0 && activeInv ? 'linear-gradient(135deg,#7c6af5,#c084fc)' : '#dee2e6', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 15, fontWeight: 700, cursor: staging.length > 0 && activeInv ? 'pointer' : 'not-allowed', flexShrink: 0, opacity: staging.length > 0 && activeInv ? 1 : 0.5 }}>
            CHECKOUT ({staging.length} customers)
          </button>

          {/* Manual comment */}
          <form onSubmit={addManualComment} style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 12, padding: 10, flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 6, textTransform: 'uppercase' }}>Manual Comment</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input name="cname" style={{ ...inp, flex: '0 0 120px' }} placeholder="FB Name" required />
              <input name="ctext" style={{ ...inp, flex: 1 }} placeholder="Comment..." />
              <button type="submit" style={{ background: '#dee2e6', border: 'none', borderRadius: 7, padding: '0 10px', color: '#212529', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>Add</button>
            </div>
          </form>
        </div>

        {/* RIGHT — Comment feed */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexShrink: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#a78bf8' }}>COMMENT FEED ({comments.length})</div>
            {activeSession?.status === 'live' && <span style={{ fontSize: 11, color: '#22c55e' }}>🟢 Live — refreshing every 5s</span>}
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {comments.length === 0 && (
              <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 12, padding: 30, textAlign: 'center', color: '#6c757d', fontSize: 13 }}>
                {'Waiting for comments...'}
              </div>
            )}
            {comments.map(c => {
              const processed = processedIds.has(c.id);
              return (
                <div key={c.id} style={{ background: processed ? '#ffffff' : '#ffffff', border: `1px solid ${processed ? '#e9ecef' : '#dee2e6'}`, borderRadius: 10, padding: '8px 12px', marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', opacity: processed ? 0.85 : 1 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                    {(c.fb_user_name || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 1, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 12, color: c.fb_blacklisted ? '#ef4444' : '#212529' }}>
                      {c.fb_blacklisted ? '⛔' : c.fb_ranking === 'platinum' ? '💎' : c.fb_ranking === 'gold' ? '🥇' : c.fb_ranking === 'silver' ? '🥈' : ''}
                      {' '}{c.fb_user_name}
                    </span>
                      {c.detected_codes?.map(code => (
                        <span key={code} style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: '#7c6af520', color: '#a78bf8' }}>{code}</span>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: '#212529', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.comment_text}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {processed ? (
                      <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>✓</span>
                    ) : c.fb_blacklisted ? (
                      <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⛔</span>
                    ) : (
                      <button onClick={() => addToStaging(c)} style={{ background: '#7c6af5', border: 'none', borderRadius: 6, padding: '5px 10px', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+</button>
                    )}
                    <button onClick={() => openAddon(c)} style={{ background: '#dee2e6', border: 'none', borderRadius: 6, padding: '5px 8px', color: '#a78bf8', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>+Add</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* BOTTOM — All live inventory */}
      <div style={{ borderTop: '1px solid #dee2e6', paddingTop: 10, marginTop: 10, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>All Live Inventory — Click to Set Active</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...inp, width: 160, padding: '5px 10px', fontSize: 12 }} placeholder="Search code..." value={invSearch} onChange={e => setInvSearch(e.target.value)} />
            <button onClick={() => setShowAddInv(true)} style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Code</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
          {filteredInv.map(item => (
            <div key={item.id} onClick={() => setActiveInv(item)} style={{ background: activeInv?.id === item.id ? '#7c6af5' : '#ffffff', border: `1.5px solid ${activeInv?.id === item.id ? '#7c6af5' : '#dee2e6'}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer', flexShrink: 0, minWidth: 120, transition: 'all .15s' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: activeInv?.id === item.id ? '#fff' : '#a78bf8' }}>Code {item.live_code}</div>
              <div style={{ fontSize: 11, color: activeInv?.id === item.id ? '#212529' : '#495057', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{item.display_name}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: activeInv?.id === item.id ? '#fff' : '#22c55e' }}>${item.selling_price.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: activeInv?.id === item.id ? '#212529' : (item.units_remaining <= 0 ? '#ef4444' : '#495057') }}>
                {item.units_sold}/{item.units_available} sold
              </div>
            </div>
          ))}
          {filteredInv.length === 0 && (
            <div style={{ color: '#6c757d', fontSize: 13, padding: '10px 0' }}>No inventory yet — click + Add Code</div>
          )}
        </div>
      </div>

      {/* Add Inventory Modal */}
      {showAddInv && (
        <div style={{ position: 'fixed', inset: 0, background: '#000b', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 16, padding: 28, width: 420 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 18 }}>Add Live Product Code</div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Live Code * (e.g. 01, 02, 88, 99, 00)</label>
              <input style={inp} value={invForm.live_code} onChange={e => setInvForm(p => ({ ...p, live_code: e.target.value }))} placeholder="01" maxLength={4} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Product *</label>
              <select style={inp} value={invForm.product_id} onChange={e => {
                const prod = products.find(p => p.id === parseInt(e.target.value));
                setInvForm(prev => ({ ...prev, product_id: e.target.value, selling_price: prod?.selling_price || '', display_name: prod?.name || '' }));
              }}>
                <option value="">Select product...</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} — ${p.selling_price}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Display Name (shown in live)</label>
              <input style={inp} value={invForm.display_name} onChange={e => setInvForm(p => ({ ...p, display_name: e.target.value }))} placeholder="Custom name for this live" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
              <div>
                <label style={lbl}>Selling Price ($) *</label>
                <input style={inp} type="number" step="0.01" value={invForm.selling_price} onChange={e => setInvForm(p => ({ ...p, selling_price: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Units for this Live *</label>
                <input style={inp} type="number" value={invForm.units_available} onChange={e => setInvForm(p => ({ ...p, units_available: e.target.value }))} placeholder="50" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={addInventory} style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', flex: 1, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Add to Live</button>
              <button onClick={() => setShowAddInv(false)} style={{ background: '#dee2e6', color: '#212529', border: 'none', borderRadius: 8, padding: '10px', flex: 1, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Addon Modal */}
      {addonModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#000b', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#ffffff', border: '1px solid #dee2e6', borderRadius: 16, padding: 28, width: 400 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Add-on for {addonModal.fb_user_name}</div>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 16 }}>Select which code to assign:</div>
            {allInventory.map(inv => (
              <div key={inv.id} onClick={() => handleAddonSelect(inv)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 6, background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div>
                  <span style={{ fontWeight: 700, color: '#a78bf8', marginRight: 8 }}>Code {inv.live_code}</span>
                  <span style={{ fontSize: 13 }}>{inv.display_name}</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#22c55e' }}>${inv.selling_price.toFixed(2)}</span>
              </div>
            ))}
            <button onClick={() => setAddonModal(null)} style={{ background: '#dee2e6', color: '#212529', border: 'none', borderRadius: 8, padding: '10px', width: '100%', fontSize: 14, cursor: 'pointer', marginTop: 8 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}